import { SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";

const containerStyle =
  (isDisabled: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    display: "flex",
    flexDirection: "row",
    position: "relative",
    background: theme.palette.buttons.bg,
    p: "20px",
    borderRadius: "18px",
    opacity: isDisabled ? 0.7 : 1,
    ...(theme.palette.mode === "light" && {
      border: "1px solid #E5E7EB",
    }),
  });

const embeddedContainerStyle =
  (isDisabled: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    display: "flex",
    flexDirection: "row",
    position: "relative",
    p: "20px",
    borderRadius: "18px",
    opacity: isDisabled ? 0.7 : 1,
    // Match tab flashcard shell/background treatment.
    background: theme.palette.flashColors.flashBg,
    border: "7.5px solid transparent",
    backgroundImage: `linear-gradient(${theme.palette.flashColors.flashBg}, ${theme.palette.flashColors.flashBg}), linear-gradient(135deg, ${theme.palette.flashColors.flash1} 0%, ${theme.palette.flashColors.flash2} 33%, ${theme.palette.flashColors.flash3} 66%, ${theme.palette.flashColors.flash4} 100%)`,
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
  });

const buttonContainerSuggestion: SxProps<Theme> = (theme: Theme) => ({
  background: theme.palette.modalColors.bgBlockColor,
  padding: "20px",
  borderRadius: "18px",
});

const messageTextStyle: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  color: theme.palette.buttons.textColor,
  mb: "12px",
  width: "100%",
});

const messageTextStyleSuggestion: SxProps<Theme> = (theme: Theme) => ({
  fontFamily: "Red Hat Display",
  color: theme.palette.footer.textInput.color,
  mb: "12px",
});

const gridStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  alignItems: "stretch",
  gap: 2,
};

const LIGHT_BUTTON_ACCENT = "#7B49C1";
const LIGHT_BUTTON_BORDER = "#E5E7EB";

const baseGetButtonStyles =
  (
    buttonColor: string,
    buttonTextColor?: string,
    isDisabled?: boolean,
    isInfoMessage?: boolean
  ): SxProps<Theme> =>
  (theme: Theme) => {
    const isLight = theme.palette.mode === "light";
    const isDefaultButton = buttonColor === theme.palette.buttons.default;
    const isChosenButton =
      buttonColor === theme.palette.buttons.chosen ||
      buttonColor === theme.palette.buttons.chosenAndCorrect;
    const resolvedTextColor =
      buttonTextColor ||
      (isLight && isChosenButton
        ? "#FFFFFF"
        : theme.palette.buttons.textColor);

    return {
      borderRadius: "12px",
      color: resolvedTextColor,
      width: "100%",
      textTransform: "none",
      fontSize: isInfoMessage ? "0.875em" : "0.875rem",
      fontWeight: 500,
      border:
        isLight && isDefaultButton
          ? `1px solid ${LIGHT_BUTTON_BORDER}`
          : "1px solid transparent",
      transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
      backgroundColor: buttonColor,
      opacity: isDisabled ? 0.9 : 1,
      pointerEvents: isDisabled ? "none" : "auto",
      height: "100%",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: buttonColor,
        color: resolvedTextColor,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: buttonColor,
        color: resolvedTextColor,
      },
      "&:disabled": {
        backgroundColor: buttonColor,
        color: resolvedTextColor,
        opacity: 0.9,
      },
      "& .markdown": {
        color: "inherit",
        maxHeight: "3.5em",
        overflowY: "auto",
        width: "100%",
        scrollbarWidth: "thin",
      },
      "& .markdown p, & .markdown em, & .markdown span": {
        color: "inherit",
        display: "inline",
      },
      "& .markdown strong": {
        color:
          isLight && isDefaultButton ? LIGHT_BUTTON_ACCENT : "inherit",
        display: "inline",
      },
    };
  };

const buttonStylesSuggestion: SxProps<Theme> = (theme: Theme) => ({
  borderRadius: "12px",
  width: "100%",
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 500,
  transition: "background-color 0.2s ease-in-out",
  border: "1px solid #C5C5C5",
  paddingY: "6px",
  color: theme.palette.header.userBlock.nameColor,
  "& .markdown": {
    color: "inherit",
    maxHeight: "3.5em",
    overflowY: "auto",
    width: "100%",
    scrollbarWidth: "thin",
  },
  "& .markdown p, & .markdown strong, & .markdown em, & .markdown span": {
    color: "inherit",
    display: "inline",
  },
});

const mobileWarningTouchProps: SxProps<Theme> = {
  touchAction: "manipulation",
  WebkitTapHighlightColor: "transparent",
  userSelect: "none",
  WebkitUserSelect: "none",
  pointerEvents: "auto",
};

const mobileWarningButtonStyle = (theme: Theme): SxProps<Theme> => {
  if (theme.palette.mode === "light") {
    return [
      baseGetButtonStyles(
        theme.palette.buttons.default,
        theme.palette.buttons.textColor,
        false,
        false
      ),
      mobileWarningTouchProps,
    ] as SxProps<Theme>;
  }

  return {
    borderRadius: "12px",
    width: "100%",
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "10px 16px",
    color: "#FFFFFF",
    backgroundColor: "#2B415E",
    border: "none",
    transition: "background-color 0.2s ease-in-out, opacity 0.2s ease-in-out",
    ...mobileWarningTouchProps,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
    },
    "&:active": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
    "& .markdown": {
      color: "inherit",
      maxHeight: "3.5em",
      overflowY: "auto",
      width: "100%",
      scrollbarWidth: "thin",
    },
    "& .markdown p, & .markdown strong, & .markdown em, & .markdown span": {
      color: "inherit",
      display: "inline",
    },
  };
};

const timeStyle: SxProps<Theme> = (theme: Theme) => ({
  mt: "7px",
  alignSelf: "flex-end",
  color: theme.palette.buttons.timeColor,
  fontSize: "12px",
  opacity: 0.8,
});

const timeStyleSystem: SxProps<Theme> = (theme: Theme) => ({
  mt: "7px",
  alignSelf: "flex-end",
  color: theme.palette.header.userBlock.linkColor,
  fontSize: "12px",
  opacity: 0.8,
});

interface ButtonStylesFunction {
  (
    buttonColor: string,
    buttonTextColor?: string,
    isDisabled?: boolean,
    isInfoMessage?: boolean
  ): SxProps<Theme>;
  getButtonColor: (
    theme: Theme,
    isChosen: boolean,
    isCorrect: boolean
  ) => string;
  buttonColors: (theme: Theme) => {
    bg: string;
    textColor: string;
    timeColor: string;
    default: string;
    chosen: string;
    correct: string;
    chosenAndCorrect: string;
  };
}

const getButtonStyles = baseGetButtonStyles as ButtonStylesFunction;

getButtonStyles.getButtonColor = (
  theme: Theme,
  isChosen: boolean,
  isCorrect: boolean
): string => {
  if (isChosen && isCorrect) {
    return theme.palette.buttons.chosenAndCorrect;
  } else if (isChosen) {
    return theme.palette.buttons.chosen;
  } else if (isCorrect) {
    return theme.palette.buttons.correct;
  }
  return theme.palette.buttons.default;
};

getButtonStyles.buttonColors = (theme: Theme) => theme.palette.buttons;

export {
  getButtonStyles,
  containerStyle,
  embeddedContainerStyle,
  messageTextStyle,
  gridStyle,
  timeStyle,
  messageTextStyleSuggestion,
  timeStyleSystem,
  buttonStylesSuggestion,
  buttonContainerSuggestion,
  mobileWarningButtonStyle,
};
